import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Page, LayoutSingleColumn, Footer, IconSpinner } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import facebookImage from '../../assets/vivianlab-brandImage.png';
import twitterImage from '../../assets/vivianlab-brandImage.png';
import { withViewport } from '../../util/uiHelpers';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { useConfiguration } from '../../context/configurationContext';
import { formatBlogPublishDate, getImagesLink } from '../../util/dataExtractors';
import { isArrayLength } from '../../util/genericHelpers';
import { convertMarkdownToReact } from '../../util/markdownHelpers';

import css from './SingleBlogPage.module.css';
import SectionHero from './SectionHero';

export const SingleBlogPageComponent = props => {
  const { scrollingDisabled, searchInProgress, blogData, searchBlogsError } = props;

  const [scroll, setScroll] = useState(false);
  const config = useConfiguration();
  const baseUrl = config.strapi.baseURL;
  const blogImages = blogData?.attributes?.images?.data;
  const title = blogData?.attributes?.title;
  const description = blogData?.attributes?.description;
  const imagesLink = getImagesLink(blogImages, baseUrl);
  const publishDate = formatBlogPublishDate(blogData);

  useEffect(() => {
    let isMounted = true;
    if (typeof window !== 'undefined') {
      const handleScroll = () => {
        if (isMounted) {
          setScroll(window.scrollY > 0);
        }
      };
      window.addEventListener('scroll', handleScroll);
      return () => {
        isMounted = false; // This is the cleanup for event listener
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const isHeaderSticky = scroll ? css.sticky : '';
  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={title}
      description={description}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: description,
        name: title,
        image: imagesLink,
      }}
    >
      <LayoutSingleColumn
        isStaticPage={true}
        className={css.pageRoot}
        topbar={<TopbarContainer isFullWidthHeader isPostLandingPage />}
        footer={<Footer />}
      >
        <div className={css.pageContent}>
          <SectionHero title={title} />
          <div className={css.contentWidth}>
            {searchInProgress ? (
              <div className={css.loader}>
                <IconSpinner />
              </div>
            ) : null}
            <div className={css.blogsContainer}>
              {publishDate ? <div className={css.blogPublishDate}>{publishDate}</div> : null}
              <div className={css.blogImage}>
                {isArrayLength(imagesLink) && <img src={imagesLink[0]} alt={title} />}
              </div>
              <div className={css.content}>
                {description ? convertMarkdownToReact(description) : ''}
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool, object } = PropTypes;

SingleBlogPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { searchInProgress, blogData, searchBlogsError } = state.SingleBlogPage;
  const { currentUser } = state.user;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    searchInProgress,
    blogData,
    searchBlogsError,
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

const SingleBlogPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl
)(SingleBlogPageComponent);

export default SingleBlogPage;
